// Biometric score engine: FED, CDI, LRM, PLI, PSI + per-shift estimates.
// Ported from the prototype; thresholds and weights are unchanged.
// Only TYPES are imported from ./data (data.ts calls these at module load).

import type { RoleType, Shift, Worker } from './data';
import type { Tone } from '../ui';

// ── FED — Fatigue Exposure Dose (0–100). Weights: HRV 45%, RHR 30%, Sleep 25% ──
export function calcFED(w: Worker): number {
  const hrvDev = w.hrv_b > 0 ? ((w.hrv_b - w.hrv) / w.hrv_b) * 100 : 0;
  const hrvScore =
    hrvDev <= 0
      ? 0
      : hrvDev <= 8
        ? Math.round(10 + (hrvDev / 8) * 15)
        : hrvDev <= 15
          ? Math.round(25 + ((hrvDev - 8) / 7) * 25)
          : hrvDev <= 22
            ? Math.round(50 + ((hrvDev - 15) / 7) * 25)
            : Math.round(Math.min(100, 75 + ((hrvDev - 22) / 5) * 25));

  const rhrDev = w.rhr_b > 0 ? ((w.rhr - w.rhr_b) / w.rhr_b) * 100 : 0;
  const rhrScore =
    rhrDev <= 0
      ? 0
      : rhrDev <= 5
        ? Math.round(10 + (rhrDev / 5) * 15)
        : rhrDev <= 10
          ? Math.round(25 + ((rhrDev - 5) / 5) * 25)
          : rhrDev <= 15
            ? Math.round(50 + ((rhrDev - 10) / 5) * 25)
            : Math.round(Math.min(100, 75 + ((rhrDev - 15) / 5) * 25));

  const slpDef = w.slp_b > 0 ? w.slp_b - w.slp : 0;
  const slp3 = w.slp7 ? w.slp7.slice(-3).reduce((a, v) => a + v, 0) / 3 : w.slp;
  const slp3Def = w.slp_b > 0 ? w.slp_b - slp3 : 0;
  let slpScore =
    slpDef <= 0
      ? 0
      : slpDef <= 0.5
        ? Math.round(10 + (slpDef / 0.5) * 10)
        : slpDef <= 1
          ? Math.round(20 + ((slpDef - 0.5) / 0.5) * 21)
          : slpDef <= 1.5
            ? Math.round(41 + ((slpDef - 1) / 0.5) * 24)
            : Math.round(Math.min(100, 65 + ((slpDef - 1.5) / 0.5) * 35));
  if (slp3Def >= 1) slpScore = Math.min(100, slpScore + 10); // sustained deficit modifier

  return Math.round(hrvScore * 0.45 + rhrScore * 0.3 + slpScore * 0.25);
}

// ── CDI — Circadian Disruption Index. Weights: HRV timing 40%, RHR curve 35%, sleep drift 25% ──
export function calcCDI(w: Worker): number {
  const peakOff = w.hrv_peak_offset || 0;
  const hrvTimingScore =
    peakOff < 1
      ? Math.round((peakOff / 1) * 15)
      : peakOff < 2
        ? Math.round(15 + (peakOff - 1) * 20)
        : peakOff < 3
          ? Math.round(35 + (peakOff - 2) * 25)
          : peakOff < 4
            ? Math.round(60 + (peakOff - 3) * 20)
            : Math.round(Math.min(100, 80 + (peakOff - 4) * 10));

  const rmse = w.rhr_curve_rmse || 0;
  const rhrCurveScore =
    rmse < 2
      ? Math.round((rmse / 2) * 15)
      : rmse < 4
        ? Math.round(15 + ((rmse - 2) / 2) * 25)
        : rmse < 6
          ? Math.round(40 + ((rmse - 4) / 2) * 25)
          : rmse < 8
            ? Math.round(65 + ((rmse - 6) / 2) * 20)
            : Math.round(Math.min(100, 85 + (rmse - 8) * 5));

  const drift = w.slp_onset_drift || 0;
  const slpEff = w.slp_efficiency || 88;
  let slpDriftScore =
    drift < 0.5
      ? Math.round((drift / 0.5) * 10)
      : drift < 1
        ? Math.round(10 + ((drift - 0.5) / 0.5) * 20)
        : drift < 2
          ? Math.round(30 + (drift - 1) * 25)
          : drift < 3
            ? Math.round(55 + (drift - 2) * 20)
            : Math.round(Math.min(100, 75 + (drift - 3) * 10));
  if (slpEff < 85) slpDriftScore = Math.min(100, slpDriftScore + 10);

  return Math.round(hrvTimingScore * 0.4 + rhrCurveScore * 0.35 + slpDriftScore * 0.25);
}

// ── LRM — Load Readiness Mismatch: shift demand minus readiness ──────────────
export interface LrmResult {
  lrm: number;
  demand: number;
  readiness: number;
  fed: number;
  cdi: number;
}

export function calcLRM(w: Worker): LrmResult {
  const fed = calcFED(w);
  const cdi = calcCDI(w);
  const readiness = Math.max(0, 100 - (fed * 0.6 + cdi * 0.4));

  const durationHrs = w.shifts[0] && w.shifts[0].in ? w.shiftHrs / (w.shifts.filter((s) => s.in).length || 1) : 8;
  const durScore = durationHrs <= 6 ? 10 : durationHrs <= 8 ? 25 : durationHrs <= 10 ? 50 : durationHrs <= 12 ? 70 : 90;
  const driveScore = w.driveKm <= 0 ? 0 : w.driveKm <= 200 ? 20 : w.driveKm <= 400 ? 45 : w.driveKm <= 600 ? 65 : 85;
  const safetyScore = w.wtrFlag ? 80 : w.consec >= 5 ? 50 : 30;
  const nightShift = w.shifts.some((s) => s.in && (s.in >= '22:00' || s.in <= '06:00'));
  const timeScore = nightShift ? 85 : w.shifts.some((s) => s.in && s.in <= '07:00') ? 60 : 20;
  const physScore = w.driveKm > 0 ? 60 : w.edaScore > 70 ? 50 : 30;

  const demand = Math.round(
    durScore * 0.25 + driveScore * 0.25 + safetyScore * 0.2 + timeScore * 0.15 + physScore * 0.15,
  );
  const lrm = Math.round(demand - readiness);
  return { lrm, demand, readiness: Math.round(readiness), fed, cdi };
}

// ── Labels + tones (prototype colour helpers → Tone) ─────────────────────────
/** LRM colour/pill: >40 red, 1–40 amber, else good (prototype lrmColor and lrmPill agree). */
export const lrmTone = (lrm: number): Tone => (lrm > 40 ? 'bad' : lrm > 0 ? 'warn' : 'good');
export const lrmLabel = (lrm: number): string =>
  lrm > 60 ? 'Critical' : lrm > 40 ? 'High' : lrm > 20 ? 'Significant' : lrm > 0 ? 'Watch' : 'OK';
export const cdiTone = (c: number): Tone => (c >= 60 ? 'bad' : c >= 35 ? 'warn' : 'good');
export const cdiLabel = (c: number): string => (c >= 60 ? 'Severe' : c >= 35 ? 'Moderate' : 'Normal');
/** Readiness 0/1/2 → good/warn/bad (prototype rdyClass pill-teal/amber/red). */
export const rdyTone = (r: number): Tone => (['good', 'warn', 'bad'] as const)[r] ?? 'muted';

export const pliLabel = (score: number): string =>
  score >= 75 ? 'Very High' : score >= 55 ? 'High' : score >= 35 ? 'Moderate' : 'Low';
export const pliTone = (score: number): Tone =>
  score >= 75 ? 'bad' : score >= 55 ? 'warn' : score >= 35 ? 'good' : 'muted';
export const psiLabel = (score: number): string =>
  score >= 75 ? 'Very High' : score >= 55 ? 'High' : score >= 35 ? 'Moderate' : 'Low';
export const psiTone = (score: number): Tone =>
  score >= 75 ? 'bad' : score >= 55 ? 'warn' : score >= 35 ? 'violet' : 'muted';
export const roleTypeLabel = (rt: RoleType): string =>
  rt === 'white' ? 'White collar' : rt === 'blue' ? 'Blue collar' : 'Mixed role';

// ── Per-shift CDI ────────────────────────────────────────────────────────────
export interface ShiftCdi {
  score: number;
  type: 'Night' | 'Early' | 'Morning' | 'Day';
  durH: number;
}

/** Estimated circadian cost of a single shift; null on a rest day. */
export function calcShiftCDI(s: Pick<Shift, 'in' | 'out'>, w: Worker): ShiftCdi | null {
  if (!s.in) return null;
  const startH = parseInt(s.in.split(':')[0]) + parseInt(s.in.split(':')[1]) / 60;
  const habitualStart = 7.0 + (w.slp_onset_drift || 0) * 0.6;

  let timingDev = Math.abs(startH - habitualStart);
  if (timingDev > 12) timingDev = 24 - timingDev; // wrap around midnight

  const isNight = startH >= 22 || startH < 4;
  const isEarly = startH >= 4 && startH < 6.5;
  const isMorning = startH >= 6.5 && startH < 8;

  let score =
    timingDev < 0.5
      ? Math.round((timingDev / 0.5) * 10)
      : timingDev < 1.5
        ? Math.round(10 + ((timingDev - 0.5) / 1) * 20)
        : timingDev < 3
          ? Math.round(30 + ((timingDev - 1.5) / 1.5) * 25)
          : timingDev < 5
            ? Math.round(55 + ((timingDev - 3) / 2) * 20)
            : Math.round(Math.min(90, 75 + (timingDev - 5) * 5));

  if (isNight) score = Math.min(100, score + 20);
  else if (isEarly) score = Math.min(100, score + 12);
  else if (isMorning) score = Math.min(100, score + 5);

  let durH = 8;
  if (s.in && s.out) {
    const [ih, im] = s.in.split(':').map(Number);
    const [oh, om] = s.out.split(':').map(Number);
    let mins = oh * 60 + om - (ih * 60 + im);
    if (mins < 0) mins += 1440; // crossed midnight
    durH = mins / 60;
  }
  if (durH > 12) score = Math.min(100, score + 15);
  else if (durH > 10) score = Math.min(100, score + 8);

  // Workers with high overall CDI pay a higher per-shift cost.
  const susceptibility = w.cdi >= 60 ? 1.15 : w.cdi >= 35 ? 1.07 : 1.0;
  score = Math.min(100, Math.round(score * susceptibility));

  const type = isNight ? 'Night' : isEarly ? 'Early' : isMorning ? 'Morning' : 'Day';
  return { score, type, durH };
}

// ── PLI — Physical Load Index ────────────────────────────────────────────────
/** Keytel kcal/min (gender-split), floored at 0. */
function keytelKcalMin(w: Worker): number {
  const hr = w.hr_work || 120;
  const wt = w.weight_kg || 75;
  const ag = w.age || 35;
  const v =
    w.gender === 'F'
      ? (-20.4022 + 0.4472 * hr - 0.1263 * wt + 0.074 * ag) / 4.184
      : (-55.0969 + 0.6309 * hr - 0.1988 * wt + 0.2017 * ag) / 4.184;
  return Math.max(0, v);
}

export function calcCals(w: Worker): number {
  const hrs = w.shiftHrs || 8;
  return Math.round(keytelKcalMin(w) * hrs * 60);
}

export interface PliResult {
  pli: number;
  hrrPct: number;
  autoScore: number;
  edaScore: number;
  kcal: number;
}

export function calcPLI(w: Worker): PliResult {
  const age = w.age || 35;
  const rhr = w.rhr || 65;
  const hrWork = w.hr_work || 120;
  const hrMax = 220 - age;

  // Karvonen % heart-rate reserve
  const hrrPct = hrMax > rhr ? Math.min(100, Math.max(0, ((hrWork - rhr) / (hrMax - rhr)) * 100)) : 50;

  // Autonomic depletion: HRV pre→post drop + recovery-HR deficit
  const hrvPre = w.hrv_pre || w.hrv || 50;
  const hrvPost = w.hrv_post || w.hrv || 50;
  const hrvDrop = hrvPre > 0 ? Math.min(100, Math.max(0, ((hrvPre - hrvPost) / hrvPre) * 100)) : 0;
  const hrRec = w.hr_rec || 30;
  const hrRecB = w.hr_rec_b || 30;
  const recDeficit = hrRecB > 0 ? Math.min(100, Math.max(0, ((hrRec - hrRecB) / hrRecB) * 100)) : 0;
  const autoScore = Math.min(100, hrvDrop * 0.65 + recDeficit * 0.35);

  const edaScore = w.edaScore || 0;
  const pli = Math.round(hrrPct * 0.5 + autoScore * 0.3 + edaScore * 0.2);

  return {
    pli: Math.min(100, pli),
    hrrPct: Math.round(hrrPct),
    autoScore: Math.round(autoScore),
    edaScore: Math.round(edaScore),
    kcal: calcCals(w),
  };
}

// ── PSI — Psychological Stress Index ─────────────────────────────────────────
export interface PsiResult {
  psi: number;
  autoScore: number;
  cogScore: number;
  patternScore: number;
  roleType: RoleType;
}

export function calcPSI(w: Worker): PsiResult {
  const rt: RoleType = w.roleType || 'mixed';

  // 1. Autonomic dysregulation (HRV suppression + RHR elevation), attenuated by physical load for field roles
  const hrvSupp = w.hrv_b > 0 ? Math.min(100, Math.max(0, ((w.hrv_b - w.hrv) / w.hrv_b) * 100)) : 0;
  const rhrElev = w.rhr_b > 0 ? Math.min(100, Math.max(0, ((w.rhr - w.rhr_b) / w.rhr_b) * 100)) : 0;
  const hrMax = 220 - (w.age || 35);
  const hrrPct =
    hrMax > (w.rhr || 65)
      ? Math.min(100, Math.max(0, (((w.hr_work || 120) - (w.rhr || 65)) / (hrMax - (w.rhr || 65))) * 100))
      : 50;
  const physConfound = rt === 'white' ? 0 : rt === 'blue' ? hrrPct * 0.6 : hrrPct * 0.35;
  const adjHRVsupp = Math.max(0, hrvSupp - physConfound * 0.01 * hrvSupp);
  const autoScore = Math.min(100, Math.round(adjHRVsupp * 0.65 + rhrElev * 0.35));

  // 2. Cognitive arousal: EDA not explained by HR elevation
  const edaRaw = w.edaScore || 0;
  const hrElev = w.hr_work_b > 0 ? Math.min(100, Math.max(0, ((w.hr_work - w.hr_work_b) / w.hr_work_b) * 100)) : 0;
  const hrCoupling = Math.min(1, hrElev / 50);
  const cogFrac =
    rt === 'white'
      ? Math.max(0.7, 1 - hrCoupling * 0.3)
      : rt === 'blue'
        ? Math.max(0.1, 1 - hrCoupling * 0.9)
        : Math.max(0.4, 1 - hrCoupling * 0.6);
  const cogScore = Math.min(100, Math.round(edaRaw * cogFrac));

  // 3. Work-pattern pressure
  const consecScore = Math.min(100, (w.consec || 0) * 12);
  const otLoad = w.otHrs > 0 ? Math.min(100, (w.otHrs / (w.normalHrs || 40)) * 300) : 0;
  const nightShifts = (w.shifts || []).filter((s) => s.in && parseInt(s.in) >= 20).length;
  const nightPenalty = nightShifts >= 2 ? 20 : nightShifts === 1 ? 10 : 0;
  const otScale = rt === 'white' ? 1.2 : rt === 'blue' ? 0.75 : 1.0;
  const patternScore = Math.min(100, Math.round(consecScore * 0.4 + otLoad * otScale * 0.4 + nightPenalty * 0.2));

  const wAuto = rt === 'white' ? 0.42 : rt === 'blue' ? 0.35 : 0.4;
  const wCog = rt === 'white' ? 0.38 : rt === 'blue' ? 0.3 : 0.35;
  const wPat = rt === 'white' ? 0.2 : rt === 'blue' ? 0.35 : 0.25;

  const psi = Math.round(autoScore * wAuto + cogScore * wCog + patternScore * wPat);
  return { psi: Math.min(100, psi), autoScore, cogScore, patternScore, roleType: rt };
}

// ── Per-shift PLI ────────────────────────────────────────────────────────────
export interface ShiftPli {
  score: number;
  kcal: number;
  durH: number;
}

/** Scales the worker's cached PLI by shift duration, type and driving; null on a rest day. */
export function calcShiftPLI(s: Pick<Shift, 'in' | 'out' | 'drive'>, w: Worker): ShiftPli | null {
  if (!s.in) return null;
  const cdi = calcShiftCDI(s, w);
  const durH = cdi ? cdi.durH : 8;
  const normalH = (w.normalHrs || 40) / 5;

  // Sub-linear (power 0.75) so long shifts add load without exploding.
  const durScale = Math.min(1.4, Math.pow(Math.max(0.1, durH / normalH), 0.75));
  const type = cdi ? cdi.type : 'Day';
  const typeScale = type === 'Night' ? 1.15 : type === 'Early' ? 1.08 : 1.0;
  const basePLI = w.pli != null ? w.pli : calcPLI(w).pli;
  const driveBonus = s.drive > 0 ? Math.min(8, Math.round(s.drive / 80)) : 0;

  const score = Math.min(100, Math.round(basePLI * durScale * typeScale + driveBonus));
  const kcal = Math.round(keytelKcalMin(w) * durH * 60);
  return { score, kcal, durH };
}
